import { Injector } from '@angular/core';
import { draggableNodeStopEvent } from '@examdojo/core/tiptap';
import { mergeAttributes, Node } from '@tiptap/core';
import { AngularNodeViewRenderer } from 'ngx-tiptap';
import { parseJsonAttr } from '../parse-json-attr.util';
import { SuperLatexNodeComponent } from './super-latex.component';
import { DEFAULT_SUPER_LATEX_PROPS } from './super-latex.model';

const superLatexClassName = 'super-latex';
const latexNodesDataAttr = 'data-latex-nodes';
const annotationsDataAttr = 'data-annotations';
const connectionsDataAttr = 'data-connections';
const layoutDataAttr = 'data-layout';
const highlightedAnchorsDataAttr = 'data-highlighted-anchors';
const customCssDataAttr = 'data-custom-css';
const customStyleDataAttr = 'data-custom-style';

export const SuperLatexExtension = (injector: Injector) => {
  return Node.create({
    name: 'superLatex',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
      return {
        latex_nodes: {
          default: DEFAULT_SUPER_LATEX_PROPS.latex_nodes,
          renderHTML: (attributes) => {
            return {
              [latexNodesDataAttr]: JSON.stringify(attributes['latex_nodes'] ?? DEFAULT_SUPER_LATEX_PROPS.latex_nodes),
            };
          },
          parseHTML: (element) => parseJsonAttr(element, latexNodesDataAttr, DEFAULT_SUPER_LATEX_PROPS.latex_nodes),
        },
        annotations: {
          default: null,
          renderHTML: (attributes) => {
            return {
              [annotationsDataAttr]: JSON.stringify(attributes['annotations'] ?? null),
            };
          },
          parseHTML: (element) => parseJsonAttr(element, annotationsDataAttr, null),
        },
        connections: {
          default: null,
          renderHTML: (attributes) => {
            return {
              [connectionsDataAttr]: JSON.stringify(attributes['connections'] ?? null),
            };
          },
          parseHTML: (element) => parseJsonAttr(element, connectionsDataAttr, null),
        },
        layout: {
          default: null,
          renderHTML: (attributes) => {
            return {
              [layoutDataAttr]: JSON.stringify(attributes['layout'] ?? null),
            };
          },
          parseHTML: (element) => parseJsonAttr(element, layoutDataAttr, null),
        },
        highlighted_anchors: {
          default: null,
          renderHTML: (attributes) => {
            return {
              [highlightedAnchorsDataAttr]: JSON.stringify(attributes['highlighted_anchors'] ?? null),
            };
          },
          parseHTML: (element) => parseJsonAttr(element, highlightedAnchorsDataAttr, null),
        },
        custom_css: {
          default: '',
          renderHTML: (attributes) => {
            return {
              [customCssDataAttr]: JSON.stringify(attributes['custom_css'] ?? ''),
            };
          },
          parseHTML: (element) => parseJsonAttr(element, customCssDataAttr, ''),
        },
        custom_style: {
          default: null,
          renderHTML: (attributes) => {
            return {
              [customStyleDataAttr]: JSON.stringify(attributes['custom_style'] ?? null),
            };
          },
          parseHTML: (element) => parseJsonAttr(element, customStyleDataAttr, null),
        },
        class: {
          renderHTML: (attributes) => {
            const otherClasses = ((attributes['class'] ?? '') as string)
              .split(' ')
              .filter((c) => c !== superLatexClassName);
            return { class: `${superLatexClassName} ${otherClasses.join(' ')}` };
          },
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: `div.${superLatexClassName}`,
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes({ class: superLatexClassName }, HTMLAttributes)];
    },

    addNodeView() {
      return AngularNodeViewRenderer(SuperLatexNodeComponent, {
        injector,
        stopEvent: draggableNodeStopEvent,
      });
    },
  });
};
